import { Search, WarningAmberOutlined } from '@mui/icons-material';
import { Box, Button, Checkbox, FormControlLabel, FormHelperText, InputAdornment, TextField, Typography, alpha, useTheme } from '@mui/material';
import { ReactNode, RefObject, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SiteNetworksCatalogue } from '../context/SiteLookupContext';
import { compareNetworkNames, normalizeSearchText } from './searchFilters';

type Props = {
  catalogue: SiteNetworksCatalogue;
  /** validation error of the selection */
  error?: string;
  onChange: (networkIds: string[]) => void;
  /** the search field, focused when the popover opens */
  searchRef: RefObject<HTMLInputElement | null>;
  value: readonly string[];
};

type Option = { id: string; name: string; unavailable?: boolean };

/**
 * Searchable checkbox list of the whole network catalogue. The selection is kept by id: the
 * search never changes it, and a selected id missing from the catalogue stays listed (and removable).
 */
const NetworkPicker = ({ catalogue, error, onChange, searchRef, value }: Props) => {
  const { t } = useTranslation();
  const { palette, borderRadius } = useTheme();

  const helperId = useId();
  const errorId = useId();

  // The search is temporary: the picker mounts with each popover, so it starts empty.
  const [query, setQuery] = useState('');

  const isCatalogueLoaded = !!catalogue.data;
  const namesById = useMemo(() => new Map((catalogue.data ?? []).map(({ id, name }) => [id, name])), [catalogue.data]);

  const selectedOptions: Option[] = useMemo(
    () =>
      value
        .map((id) => {
          const name = namesById.get(id);
          // Before the catalogue is loaded the id is the only label we have: never drop the selection.
          return name !== undefined ? { id, name } : { id, name: id, unavailable: isCatalogueLoaded };
        })
        .sort((a, b) => Number(!!a.unavailable) - Number(!!b.unavailable) || compareNetworkNames(a.name, b.name)),
    [isCatalogueLoaded, namesById, value],
  );

  const normalizedQuery = normalizeSearchText(query);
  const otherOptions: Option[] = useMemo(() => {
    const selectedIds = new Set(value);

    return (catalogue.data ?? [])
      .filter(({ id, name }) => !selectedIds.has(id) && normalizeSearchText(name).includes(normalizedQuery))
      .sort((a, b) => compareNetworkNames(a.name, b.name));
  }, [catalogue.data, normalizedQuery, value]);

  // Toggling moves the option between Selected and the other group, which remounts its checkbox:
  // without this, the focus would fall outside the popover and Esc would no longer close it.
  const checkboxRefs = useRef(new Map<string, HTMLInputElement>());
  const pendingFocusId = useRef<string | null>(null);

  // Runs after every commit: a no-op unless a toggle is waiting for its checkbox to remount.
  useLayoutEffect(() => {
    const id = pendingFocusId.current;
    if (id === null) return;
    pendingFocusId.current = null;
    // An unticked option hidden by the search has no checkbox left: fall back to the search field.
    (checkboxRefs.current.get(id) ?? searchRef.current)?.focus();
  });

  const toggle = (id: string, checked: boolean) => {
    pendingFocusId.current = id;
    onChange(checked ? [...value, id] : value.filter((selectedId) => selectedId !== id));
  };

  const clearSelection = () => {
    // the button is disabled once the selection is empty: keep the focus in the picker
    searchRef.current?.focus();
    onChange([]);
  };

  const renderOption = (option: Option) => (
    <FormControlLabel
      key={option.id}
      control={
        <Checkbox
          checked={value.includes(option.id)}
          inputRef={(input: HTMLInputElement | null) => {
            if (input) checkboxRefs.current.set(option.id, input);
            else checkboxRefs.current.delete(option.id);
          }}
          onChange={(_, checked) => toggle(option.id, checked)}
          size="small"
          sx={{ padding: 1 }}
        />
      }
      label={
        option.unavailable ? (
          <Box display="flex" alignItems="center" gap={0.5}>
            <WarningAmberOutlined fontSize="small" sx={{ color: 'warning.dark' }} aria-hidden />
            <span>
              {t('siteLookup.filters.network.unavailable')}{' '}
              <Typography component="span" variant="caption" color="text.secondary">
                ({option.id})
              </Typography>
            </span>
          </Box>
        ) : (
          option.name
        )
      }
      sx={{
        alignItems: 'center',
        borderRadius: borderRadius.sm,
        marginX: 0,
        minHeight: 40,
        paddingRight: 1,
        width: '100%',
        '&:hover': { backgroundColor: alpha(palette.primary.main, 0.05) },
        '& .MuiFormControlLabel-label': { fontSize: 14, overflowWrap: 'anywhere', paddingY: 0.5 },
      }}
    />
  );

  const group = (legend: ReactNode, children: ReactNode) => (
    <Box component="fieldset" border={0} margin={0} minWidth={0} padding={0}>
      <Typography component="legend" color="text.secondary" fontSize={12} fontWeight={700} paddingX={1} paddingTop={1} paddingBottom={0.5}>
        {legend}
      </Typography>
      {children}
    </Box>
  );

  return (
    <Box display="flex" flexDirection="column" minHeight={0}>
      <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
        <Typography color="text.secondary" fontSize={13}>
          {catalogue.data ? t('siteLookup.filters.network.total', { count: catalogue.data.length }) : ''}
        </Typography>
        <Button size="small" disabled={!value.length} onClick={clearSelection}>
          {t('siteLookup.filters.network.clearSelection')}
        </Button>
      </Box>

      <TextField
        fullWidth
        inputRef={searchRef}
        onChange={(event) => setQuery(event.target.value)}
        placeholder={t('siteLookup.filters.network.searchPlaceholder')}
        size="small"
        value={query}
        sx={{ marginTop: 0.5 }}
        slotProps={{
          htmlInput: {
            'aria-label': t('siteLookup.filters.network.search'),
            'aria-describedby': error ? `${errorId} ${helperId}` : helperId,
          },
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          },
        }}
      />
      <FormHelperText id={helperId} sx={{ marginX: 0 }}>
        {t('siteLookup.filters.network.helper')}
      </FormHelperText>
      {error && (
        <FormHelperText id={errorId} error sx={{ marginX: 0 }}>
          {error}
        </FormHelperText>
      )}

      <Box maxHeight={{ xs: 'min(260px, 40vh)', sm: 260 }} overflow="auto" marginTop={1} marginX={-1}>
        {!!selectedOptions.length && group(t('siteLookup.filters.network.selectedGroup', { count: selectedOptions.length }), selectedOptions.map(renderOption))}

        {group(
          normalizedQuery ? t('siteLookup.filters.network.searchResults') : t('siteLookup.filters.network.otherGroup'),
          <>
            {catalogue.fetching && !catalogue.data && (
              <Typography color="text.secondary" fontSize={14} paddingX={1} paddingY={1}>
                {t('siteLookup.filters.network.loading')}
              </Typography>
            )}

            {catalogue.error && !catalogue.data && !catalogue.fetching && (
              <Box display="flex" alignItems="center" gap={1} paddingX={1} paddingY={1}>
                <Typography color="error" fontSize={14} role="alert">
                  {t('siteLookup.filters.network.loadError')}
                </Typography>
                <Button size="small" onClick={catalogue.retry}>
                  {t('siteLookup.filters.retry')}
                </Button>
              </Box>
            )}

            {isCatalogueLoaded && !otherOptions.length && !!normalizedQuery && (
              <Typography color="text.secondary" fontSize={14} paddingX={1} paddingY={1}>
                {t('siteLookup.filters.network.noResults')}
              </Typography>
            )}

            {otherOptions.map(renderOption)}
          </>,
        )}
      </Box>

      <Typography aria-live="polite" color="text.secondary" fontSize={13} marginTop={1}>
        {t('siteLookup.filters.network.selectedCount', { count: value.length })}
      </Typography>
    </Box>
  );
};

export default NetworkPicker;
